import React, { useEffect, useState } from 'react';
import { Badge, Box, Card, CardContent, Typography, TextField, Button, Alert, Grid, Tab, Tabs } from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useParams } from 'react-router-dom'; // capturar o ID
import { useHistory } from 'react-router-dom';
import { token } from '../../../../../api';
import api from '../../../../services/api';
import CustomFieldInput from '../shared/CustomFieldInput.js';
import { isFieldVisible } from '../shared/fieldVisibility';
import OrganizationDocuments from './OrganizationDocuments';

const EditOrganization = () => {
    const { id } = useParams();

    const [organization, setOrganization] = useState({
        name: '',
        description: '',
        cnpj: '',
        email: '',
        phone: '',
        address: ''
    });

    const [loading, setLoading] = useState(true);
    const [appId, setAppId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });

    const [fieldsConfig, setFieldsConfig] = useState([]);
    const [fields, setFields] = useState({});
    const [tab, setTab] = useState(0);
    const [documentCount, setDocumentCount] = useState(0);

    const history = useHistory();

    // 🔹 Buscar app padrão
    const pegar_app_padrao = async () => {
        try {
            const response = await api.get('v1/apps/default/');
            const data = response.data;

            if (data?.app?.id) {
                setAppId(data.app.id);
                loadFields(data.app.id);
            } else {
                setStatus({ type: 'error', message: 'App padrão não encontrado' });
            }
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao buscar app padrão' });
        }
    };

    // 🔹 Buscar definições de campos customizados
    const loadFields = async (currentAppId) => {
        try {
            const response = await api.get(`v1/apps/${currentAppId}/organization_fields/`);
            setFieldsConfig(response.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    // 🔹 INIT
    useEffect(() => {
        if (!localStorage.getItem('access_token')) return;
        pegar_app_padrao();
    }, []);

    // 🔥 CORRETO: depende de appId e id
    useEffect(() => {
        if (!appId || !id) return;
        fetchOrganization();
        fetchDocumentCount();
    }, [appId, id]);

    // Contador pra deixar visível, já na aba "Dados", que existem
    // documentos cadastrados - sem isso a aba "Documentos" parecia uma
    // aba vazia/secundária e passava batido.
    const fetchDocumentCount = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/documents/?organization=${id}`);
            setDocumentCount((response.data?.documents || []).length);
        } catch (error) {
            console.error('Erro ao contar documentos', error);
        }
    };

    // 🔹 Buscar organização
    const fetchOrganization = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/organizations/${id}/`);

            if (response.data?.organization) {
                const { custom_fields, ...rest } = response.data.organization;
                setOrganization(rest);
                setFields(custom_fields || {});
            }

            setLoading(false);
        } catch (error) {
            console.error('Erro ao buscar organização:', error);
            setLoading(false);
        }
    };

    // 🔹 Form change
    const handleChange = (e) => {
        setOrganization({
            ...organization,
            [e.target.name]: e.target.value
        });
    };

    // 🔹 Submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!appId) return;

        setStatus({ type: '', message: '' });

        try {
            const visibleNames = new Set(fieldsConfig.filter((f) => isFieldVisible(f, fieldsConfig, fields)).map((f) => f.name));
            const fieldsToSend = Object.fromEntries(Object.entries(fields).filter(([name]) => visibleNames.has(name)));

            const response = await api.patch(`v1/apps/${appId}/organizations/${id}/`, { ...organization, fields: fieldsToSend });

            if (response.data) {
                setStatus({
                    type: 'success',
                    message: 'Organização atualizada com sucesso!'
                });
            }
        } catch (error) {
            console.error(error);
            setStatus({
                type: 'error',
                message: error.response?.data?.detail || 'Erro ao atualizar organização.'
            });
        }
    };

    // 🔹 Delete
    const deleteOrganization = async () => {
        if (!appId) return;

        try {
            await api.delete(`v1/apps/${appId}/organizations/${id}/`);

            history.push(`/dashboard/config/organizations/view`);
        } catch (error) {
            console.error('Erro ao deletar organização:', error);
        }
    };

    // 🔹 Voltar
    const backPage = () => {
        history.push(`/dashboard/config/organizations/view`);
    };

    // 🔹 Loading
    if (loading) {
        return <Typography>Carregando...</Typography>;
    }

    if (!organization) {
        return <Typography>Organização não encontrada</Typography>;
    }

    return (
        <Box
            sx={{
                position: 'relative',
                minHeight: '100%',
                background: 'linear-gradient(135deg, #0f172a, #1e293b)',
                p: 3,
                borderRadius: '16px'
            }}
        >
            {/* HEADER */}
            <Typography
                variant="h3"
                sx={{
                    mb: 3,
                    fontWeight: 700,
                    background: 'linear-gradient(90deg, #38bdf8, #6366f1)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '0.5px'
                }}
            >
                Editar Organização
            </Typography>

            {/* ALERT */}
            {status.message && (
                <Alert
                    severity={status.type}
                    sx={{
                        mb: 2,
                        borderRadius: '12px',
                        backdropFilter: 'blur(10px)'
                    }}
                >
                    {status.message}
                </Alert>
            )}

            {/* CARD */}
            <Card
                sx={{
                    borderRadius: '20px',
                    backdropFilter: 'blur(20px)',
                    background: 'rgba(255,255,255,0.05)',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
                    border: '1px solid rgba(255,255,255,0.1)'
                }}
            >
                <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    variant="fullWidth"
                    TabIndicatorProps={{ sx: { height: 3, borderRadius: '3px 3px 0 0' } }}
                    sx={{
                        borderBottom: '1px solid rgba(255,255,255,0.12)',
                        '& .MuiTab-root': {
                            color: '#aaa',
                            fontWeight: 700,
                            fontSize: '1rem',
                            textTransform: 'none',
                            py: 2.5,
                            gap: 1
                        },
                        '& .Mui-selected': { color: '#38bdf8 !important' },
                        '& .MuiTabs-indicator': { backgroundColor: '#38bdf8' }
                    }}
                >
                    <Tab icon={<BadgeOutlinedIcon />} iconPosition="start" label="Dados da empresa" />
                    <Tab
                        icon={
                            <Badge badgeContent={documentCount} color="primary" showZero={false}>
                                <DescriptionOutlinedIcon />
                            </Badge>
                        }
                        iconPosition="start"
                        label="Documentos pra enviar"
                    />
                </Tabs>
                <CardContent sx={{ display: tab === 1 ? 'none' : 'block' }}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            {[
                                { name: 'name', label: 'Nome', required: true },
                                { name: 'description', label: 'Descrição' },
                                { name: 'cnpj', label: 'CNPJ/CPF' },
                                { name: 'email', label: 'Email', type: 'email' },
                                { name: 'phone', label: 'Telefone (WhatsApp)' },
                                { name: 'address', label: 'Endereço' }
                            ].map((field, index) => (
                                <Grid item xs={12} sm={6} key={index}>
                                    <TextField
                                        {...field}
                                        fullWidth
                                        value={organization[field.name]}
                                        onChange={handleChange}
                                        variant="outlined"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                background: 'rgba(255,255,255,0.08)', // fundo escuro translúcido
                                                color: '#fff', // texto digitado

                                                '& fieldset': {
                                                    borderColor: 'rgba(255,255,255,0.2)'
                                                },

                                                '&:hover fieldset': {
                                                    borderColor: '#38bdf8'
                                                },

                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#6366f1',
                                                    boxShadow: '0 0 10px rgba(99,102,241,0.6)'
                                                }
                                            },

                                            // TEXTO DIGITADO
                                            '& input': {
                                                color: '#fff'
                                            },

                                            // LABEL (ANTES DE CLICAR)
                                            '& .MuiInputLabel-root': {
                                                color: '#ccc'
                                            },

                                            // LABEL QUANDO FOCA
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: '#38bdf8'
                                            },

                                            // LABEL QUANDO SHRINK (subida)
                                            '& .MuiInputLabel-shrink': {
                                                color: '#aaa'
                                            }
                                        }}
                                    />
                                </Grid>
                            ))}

                            {fieldsConfig.filter((f) => isFieldVisible(f, fieldsConfig, fields)).map((f) => (
                                <CustomFieldInput
                                    key={f.id}
                                    field={f}
                                    value={fields[f.name]}
                                    onChange={(value) => setFields({ ...fields, [f.name]: value })}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            background: 'rgba(255,255,255,0.08)',
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                            '&:hover fieldset': { borderColor: '#38bdf8' },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#6366f1',
                                                boxShadow: '0 0 10px rgba(99,102,241,0.6)'
                                            }
                                        },
                                        '& input': { color: '#fff' },
                                        '& .MuiInputLabel-root': { color: '#ccc' },
                                        '& .MuiInputLabel-root.Mui-focused': { color: '#38bdf8' },
                                        '& .MuiInputLabel-shrink': { color: '#aaa' }
                                    }}
                                />
                            ))}

                            {/* BOTÕES */}
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        gap: 2,
                                        mt: 2
                                    }}
                                >
                                    {/* DELETE */}
                                    <Button
                                        onClick={deleteOrganization}
                                        sx={{
                                            borderRadius: '12px',
                                            px: 3,
                                            py: 1,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
                                            color: '#fff',
                                            boxShadow: '0 8px 20px rgba(239,68,68,0.4)',
                                            transition: '0.3s',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 12px 30px rgba(239,68,68,0.6)'
                                            }
                                        }}
                                    >
                                        🗑 Apagar
                                    </Button>

                                    {/* VOLTAR */}
                                    <Button
                                        variant="outlined"
                                        onClick={backPage}
                                        sx={{
                                            borderRadius: '12px',
                                            px: 3,
                                            py: 1,
                                            color: '#ccc',
                                            borderColor: '#555',
                                            textTransform: 'none',
                                            '&:hover': {
                                                borderColor: '#999',
                                                background: 'rgba(255,255,255,0.05)'
                                            }
                                        }}
                                    >
                                        Voltar
                                    </Button>

                                    {/* SALVAR */}
                                    <Button
                                        type="submit"
                                        sx={{
                                            borderRadius: '12px',
                                            px: 4,
                                            py: 1,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            background: 'linear-gradient(135deg, #22c55e, #4ade80)',
                                            color: '#000',
                                            boxShadow: '0 8px 20px rgba(34,197,94,0.4)',
                                            transition: '0.3s',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 12px 30px rgba(34,197,94,0.6)'
                                            }
                                        }}
                                    >
                                        💾 Salvar
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>

                {tab === 1 && (
                    <CardContent>
                        <OrganizationDocuments appId={appId} organizationId={id} />
                    </CardContent>
                )}
            </Card>
        </Box>
    );
};

export default EditOrganization;
